import React, { useState, useEffect, useRef } from "react";
import "../Styles/User_destination.css";
import api from "../Api.js";
import ImageLightbox from "../Components/Helping Component/ImageZoom.jsx";
import Skeleton from "@mui/material/Skeleton";

// Basic skeleton for destination cards
const DestinationSkeleton = () => (
  <div className="destination_card" style={{ pointerEvents: "none" }}>
    <Skeleton variant="rectangular" width="100%" height={200} animation="wave" />
    <Skeleton variant="text" width="60%" height={30} style={{ marginTop: "10px", marginLeft: "10px" }} />
  </div>
);

function User_Destinations() {
  const [popularDestination, setPopularDestination] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  
  // Pagination & Loading States
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 5; // Number of destinations to fetch per request

  const sliderRef = useRef(null);
  
  // Synchronous lock to prevent duplicate API calls during fast scrolling
  const isFetchingRef = useRef(false);

  // ✅ Unified Fetch Function (Handles Initial Load & Pagination)
  const fetchDestinations = async (currentPage, isInitial = false) => {
    if (isFetchingRef.current) return; // Block if already fetching
    isFetchingRef.current = true;

    if (isInitial) setIsLoading(true);
    else setIsFetchingMore(true);

    try {
      // NOTE: Ensure your backend accepts page and limit query parameters
      const response = await api.get(`/destinations?page=${currentPage}&limit=${LIMIT}`, {
        headers: {
          "X-API-TOKEN":
            "50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5",
        },
      });

      if (response.data.status) {
        const newData = response.data.data || [];

        // If we received fewer items than the limit, we've reached the end
        if (newData.length < LIMIT) {
          setHasMore(false);
        }

        if (isInitial) {
          setPopularDestination(newData);
        } else {
          // Append new data and prevent duplicates
          setPopularDestination((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewData = newData.filter((item) => !existingIds.has(item.id));
            return [...prev, ...uniqueNewData];
          });
        }
      }
    } catch (error) {
      console.log("API Error:", error);
    } finally {
      isFetchingRef.current = false; // Unlock
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  // ✅ Initial Fetch
  useEffect(() => {
    fetchDestinations(1, true);
  }, []);

  // ✅ Fetch when page changes (triggered by scroll)
  useEffect(() => {
    if (page > 1) {
      fetchDestinations(page, false);
    }
  }, [page]);

  // ✅ Scroll Event Handler for Infinite Scroll
  const handleScroll = () => {
    if (!sliderRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;

    // Check if scrolled within 150px of the right edge
    if (scrollLeft + clientWidth >= scrollWidth - 150) {
      // Check refs and states synchronously
      if (!isFetchingRef.current && hasMore && !isLoading && !isFetchingMore) {
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  const handleOpenGallery = async (item) => {
    try {
      const response = await api.get(`/destinations/${item.id}`, {
        headers: {
          "X-API-TOKEN":
            "50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5",
        },
      });

      const images = [item.cover_photo, ...(response.data.data.photos || [])];
      setCurrentImages(images);
      setLightboxOpen(true);
    } catch (error) {
      console.log("Error fetching destination details:", error);
    }
  };

  // Navigation handlers
  const nextSlide = () => {
    sliderRef.current?.scrollBy({
      left: sliderRef.current.offsetWidth / 4,
      behavior: "smooth",
    });
  };

  const prevSlide = () => {
    sliderRef.current?.scrollBy({
      left: -sliderRef.current.offsetWidth / 4,
      behavior: "smooth",
    });
  };

  return (
    <section className="user_destinations">
      <ImageLightbox
        open={lightboxOpen}
        setOpen={setLightboxOpen}
        images={currentImages}
      />

      <div className="destination_header">
        <h3 className="destination_headline">Popular Destinations</h3>
        <div className="destination_controls">
          <button onClick={prevSlide} aria-label="Previous">
            ◀
          </button>
          <button onClick={nextSlide} aria-label="Next">
            ▶
          </button>
        </div>
      </div>

      {/* Added onScroll event here */}
      <div 
        className="destination_track" 
        ref={sliderRef}
        onScroll={handleScroll}
      >
        {isLoading ? (
          // Initial Loading Skeletons
          Array.from(new Array(6)).map((_, index) => (
            <DestinationSkeleton key={`skeleton-${index}`} />
          ))
        ) : (
          // Actual Data
          popularDestination?.map((item) => (
            <div
              className="destination_card"
              key={item.id}
              onClick={() => handleOpenGallery(item)}
            >
              <img src={item.cover_photo} alt={item.name} loading="lazy" />
              <h3>{item.name}</h3>
            </div>
          ))
        )}

        {/* Loading Skeleton at the end of the slider when fetching more */}
        {isFetchingMore && (
          <div style={{ minWidth: "250px", height: "100%" }}>
            <DestinationSkeleton />
          </div>
        )}
      </div>
    </section>
  );
}

export default User_Destinations;